import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Message } from "@scrile/api-provider/dist/api/MessagesProvider";
import { User } from "@scrile/api-provider/dist/api/UserProvider";
import { createPortal } from "react-dom";
import { useTranslation } from "locales";
import useLinkPreview from "hooks/useLinkPreview";
import { getAttachmentViews } from "lib/attachmentViews";
import { hapticLight, hapticMedium } from "lib/haptics";
import { getMessageMetadata, stripMetaPrefix } from "lib/messageMetadata";
import providers from "lib/providers";
import { get24formatTime } from "lib/timeHelper";
import { OptimisticMessage } from "types";
import BaseAvatar from "components/BaseAvatar";
import EditHistoryModal from "components/EditHistoryModal";
import ImageLightbox from "components/ImageLightbox";
import LinkPreview from "components/LinkPreview";
import MentionText from "components/MentionText";
import MessageAttachments from "components/MessageAttachments";
import ReactionBar from "components/ReactionBar";
import ReactionPicker from "components/ReactionPicker";
import VideoMessagePlayer from "components/VideoMessagePlayer";
import VoiceMessagePlayer from "components/VoiceMessagePlayer";
import "./style.scss";

// Reorder `incoming` (server truth) to match the chip order currently on
// screen: existing emojis keep their position, new ones append at the end.
// The server's own ordering differs from click order, so replacing the array
// wholesale made reaction chips visibly jump a beat after each click.
function mergeReactionsKeepingOrder(current: any[], incoming: any[]): any[] {
  const incomingByEmoji = new Map(incoming.map((r: any) => [r.emoji, r]));
  const merged: any[] = [];
  for (const r of current) {
    const fresh = incomingByEmoji.get(r.emoji);
    if (fresh) {
      merged.push(fresh);
      incomingByEmoji.delete(r.emoji);
    }
  }
  for (const r of incoming) {
    if (incomingByEmoji.has(r.emoji)) merged.push(r);
  }
  // Identity bail-out: element refs come straight from `current`/`incoming`,
  // so a cheap ref-equality pass lets React skip a re-render when nothing
  // actually changed (parents rebuild message objects on every list merge).
  if (merged.length === current.length && merged.every((r, i) => r === current[i])) {
    return current;
  }
  return merged;
}

// Elements inside a bubble that own their tap/click behaviour — the
// tap-to-open-menu and right-click paths must not hijack them.
const BUBBLE_INTERACTIVE_SELECTOR =
  "a, button, img, video, audio, input, textarea, .message-bubble__reply-quote, .reaction-bar, " +
  ".voice-player, .video-message-player, .link-preview, .message-bubble__thread-indicator, " +
  ".message-bubble__edited, .message-attachments__file, " +
  // @mention spans and inline code-copy own their onClick — the tap-to-menu
  // and right-click paths must not preventDefault over them.
  ".mention-text__highlight, .mention-text__link, .mention-text__code-copy";

// Timestamp of the most recent scroll anywhere on the page (capture phase).
// Used to treat a tap that merely STOPS a momentum fling as inert instead of
// opening the action menu — native messengers behave the same way.
let lastPageScrollTs = 0;
if (typeof window !== "undefined") {
  window.addEventListener(
    "scroll",
    () => {
      lastPageScrollTs = Date.now();
    },
    { capture: true, passive: true }
  );
}

interface Props {
  message: Message;
  authUser: User | null;
  isOwn: boolean;
  showAvatar: boolean;
  showName: boolean;
  isStart: boolean;
  isEnd: boolean;
  rendered?: (ref: HTMLDivElement | null) => void;
  onMessageUpdated?: (msg: Message) => void;
  onMessageDeleted?: (messageId: string) => void;
  opponentMinUnreadMessageId?: string;
  searchQuery?: string;
  onReply?: (message: Message) => void;
  onForward?: (message: Message) => void;
  onPin?: (message: Message) => void;
  onOpenThread?: (message: Message) => void;
  // Reply-quote click: the parent pages history until the quoted message is
  // loaded and highlights it. Without it (e.g. ThreadPanel) falls back to a
  // best-effort DOM scroll.
  onQuoteClick?: (messageId: string) => void;
  isPinned?: boolean;
  threadReplyCount?: number;
  // Moderator capability: when true, the Delete action is available on
  // messages authored by OTHER users as well (group / channel owner).
  // Edit is intentionally NOT gated by this — a moderator should not
  // silently rewrite another user's text.
  canModerate?: boolean;
  // --- Multi-select mode ---
  // When `selectionMode` is true the bubble behaves as a checkbox: taps
  // toggle selection, long-press / swipe / nested interactions are
  // suppressed via a transparent overlay. The parent chat page owns the
  // `selectedIds` Set and handles bulk actions.
  selectionMode?: boolean;
  selected?: boolean;
  onToggleSelection?: (message: Message) => void;
  // Entry point into selection mode from the long-press context menu.
  onEnterSelection?: (message: Message) => void;
}

function MessageBubble({
  message,
  authUser,
  isOwn,
  showAvatar,
  showName,
  isStart,
  isEnd,
  rendered,
  onMessageUpdated,
  onMessageDeleted,
  opponentMinUnreadMessageId,
  searchQuery,
  onReply,
  onForward,
  onPin,
  onOpenThread,
  onQuoteClick,
  isPinned,
  threadReplyCount,
  canModerate,
  selectionMode,
  selected,
  onToggleSelection,
  onEnterSelection,
}: Props) {
  const { t } = useTranslation();
  const canDelete = isOwn || !!canModerate;
  const time = useMemo(() => get24formatTime(message.time), [message.time]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [reactions, setReactions] = useState<any[]>((message as any).reactions || []);
  // Sync reactions when message prop updates (e.g. other user adds reaction).
  // Merged instead of replaced so the chip order the user currently sees is
  // preserved — the server's ordering differs and would make chips jump.
  useEffect(() => {
    setReactions((prev) => mergeReactionsKeepingOrder(prev, (message as any).reactions || []));
  }, [(message as any).reactions]);
  const [showEditHistory, setShowEditHistory] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const editInputRef = useRef<HTMLTextAreaElement>(null);
  // Long-press context menu state.
  // - `ctxMenu` is the ACTUAL placed position (null when closed, or when the
  //   menu has just mounted but hasn't been measured yet).
  // - `ctxAnchor` is the pressed bubble's bounding box, captured at the moment
  //   long-press fires. Layout effect below uses it together with the menu's
  //   real rendered size (via ctxMenuRef) to decide above / below / clamp.
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number } | null>(null);
  const [ctxAnchor, setCtxAnchor] = useState<{ left: number; right: number; top: number; bottom: number } | null>(null);
  const ctxMenuRef = useRef<HTMLDivElement>(null);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Whether the long-press timer already opened the menu for the current
  // touch — the tap-to-open path in touchEnd must not open it twice.
  const longPressFired = useRef(false);
  const touchStartTime = useRef(0);
  // Snapshot of the page's last-scroll timestamp AT touchstart. Measuring
  // scroll-recency at touchend is wrong: a fling that stops on touchstart
  // emits no more scroll events, so by touchend the delta has grown to the
  // press duration and a 150–350ms "stop the scroll" press reads as a fresh
  // tap → the action menu pops. Comparing against the touchstart snapshot
  // classifies it correctly.
  const scrollTsAtTouchStart = useRef(0);

  // Swipe-to-reply state
  const swipeStartX = useRef<number | null>(null);
  const swipeStartY = useRef<number | null>(null);
  const swipeLocked = useRef<"horizontal" | "vertical" | null>(null);
  const swipeOffsetRef = useRef(0);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const swipeThreshold = 60;

  // Cleanup long-press timer on unmount
  useEffect(() => {
    return () => {
      if (longPressTimer.current) clearTimeout(longPressTimer.current);
    };
  }, []);

  // Fully dismiss the context menu (both the anchor and the placed position)
  // so the outside-click effect and the Portal both unmount cleanly.
  const closeCtxMenu = useCallback(() => {
    setCtxMenu(null);
    setCtxAnchor(null);
  }, []);

  // Open the action menu anchored to the bubble's current rect. Shared by
  // long-press, quick-tap and right-click; real placement happens in the
  // layout effect below once the menu's rendered size is known.
  const openMenuFromRect = useCallback((el: HTMLElement) => {
    const rect = el.getBoundingClientRect();
    setCtxAnchor({ left: rect.left, right: rect.right, top: rect.top, bottom: rect.bottom });
  }, []);

  // Close context menu on any outside click/touch
  useEffect(() => {
    if (!ctxAnchor) return;
    const close = (e: MouseEvent | TouchEvent) => {
      if (ctxMenuRef.current && ctxMenuRef.current.contains(e.target as Node)) return;
      closeCtxMenu();
    };
    // Also dismiss on any scroll / viewport change: the menu is anchored
    // to a snapshot of the bubble rect in viewport coordinates, so once
    // the page scrolls or rotates it would float in the wrong place.
    // Simply closing is both correct and matches iOS / Telegram behaviour.
    const dismiss = () => closeCtxMenu();
    document.addEventListener("mousedown", close);
    document.addEventListener("touchstart", close);
    window.addEventListener("scroll", dismiss, true); // capture: catch inner scrollers
    window.addEventListener("resize", dismiss);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("touchstart", close);
      window.removeEventListener("scroll", dismiss, true);
      window.removeEventListener("resize", dismiss);
    };
  }, [ctxAnchor, closeCtxMenu]);

  // Measure the menu AFTER it mounts and place it relative to the pressed
  // bubble using its REAL rendered height. This matters because the menu's
  // height varies a lot depending on which optional actions are available
  // (Reply / Forward / Pin / Edit / Delete / Copy / …). A hardcoded estimate
  // was too pessimistic and forced the menu below the bubble on most phones.
  useLayoutEffect(() => {
    if (!ctxAnchor || !ctxMenuRef.current) return;
    const menuEl = ctxMenuRef.current;
    const menuW = menuEl.offsetWidth;
    const menuH = menuEl.offsetHeight;
    const pad = 8;
    const gap = 6;

    // Horizontal: align with the bubble side (left for incoming, right for
    // own), then clamp to viewport so the menu is never off-screen.
    let x = isOwn ? ctxAnchor.right - menuW : ctxAnchor.left;
    x = Math.max(pad, Math.min(x, window.innerWidth - menuW - pad));

    // Vertical: prefer ABOVE the bubble (classic Telegram / WhatsApp).
    // Fall back to BELOW when there isn't enough room above; clamp to
    // viewport as a final safety net so the menu is always fully visible.
    const spaceAbove = ctxAnchor.top - pad;
    const spaceBelow = window.innerHeight - ctxAnchor.bottom - pad;
    let y: number;
    if (spaceAbove >= menuH + gap) {
      y = ctxAnchor.top - menuH - gap;
    } else if (spaceBelow >= menuH + gap) {
      y = ctxAnchor.bottom + gap;
    } else {
      // Neither side fits; pin to the top of the viewport. CSS
      // `max-height: calc(100vh - 16px)` + `overflow-y:auto` on the menu
      // keeps it scrollable in that case.
      y = pad;
    }
    setCtxMenu({ x, y });
  }, [ctxAnchor, isOwn]);

  // Touch handling: long-press for context menu + swipe-right for reply.
  // In selection mode we short-circuit both — the overlay handles taps and
  // there is no use case for opening the action menu on already-selected
  // bubbles.
  const handleBubbleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (selectionMode) return;
      swipeLocked.current = null;
      longPressFired.current = false;
      touchStartTime.current = Date.now();
      scrollTsAtTouchStart.current = lastPageScrollTs;
      const touch = e.touches[0];
      swipeStartX.current = touch.clientX;
      swipeStartY.current = touch.clientY;
      // Capture the bubble wrapper synchronously — this tree runs React 16,
      // where SyntheticEvents are pooled and recycled after the handler
      // returns, so the setTimeout below must not touch `e`.
      const bubbleEl = e.currentTarget as HTMLElement;
      longPressTimer.current = setTimeout(() => {
        hapticMedium();
        longPressFired.current = true;
        openMenuFromRect(bubbleEl);
      }, 500);
    },
    [selectionMode, openMenuFromRect]
  );

  const handleBubbleTouchMove = useCallback((e: React.TouchEvent) => {
    if (swipeStartX.current === null || swipeStartY.current === null) return;
    const touch = e.touches[0];
    const dx = touch.clientX - swipeStartX.current;
    const dy = touch.clientY - swipeStartY.current;

    // Lock direction after initial movement
    if (!swipeLocked.current && (Math.abs(dx) > 10 || Math.abs(dy) > 10)) {
      swipeLocked.current = Math.abs(dx) > Math.abs(dy) ? "horizontal" : "vertical";
    }

    // Cancel long-press on any movement
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }

    // Only handle horizontal swipes (right direction)
    if (swipeLocked.current === "horizontal" && dx > 0) {
      const offset = Math.min(dx, 80);
      swipeOffsetRef.current = offset;
      setSwipeOffset(offset);
    }
  }, []);

  const handleBubbleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
      }
      // Read from ref — always the current value, never stale from a previous render closure
      if (swipeOffsetRef.current >= swipeThreshold && onReply) {
        hapticLight();
        onReply(message);
      }
      const wasSwipe = swipeLocked.current !== null || swipeOffsetRef.current > 0;
      swipeOffsetRef.current = 0;
      setSwipeOffset(0);
      swipeStartX.current = null;
      swipeStartY.current = null;
      swipeLocked.current = null;
      // Quick tap (no movement, no long-press) opens the action menu — far
      // more discoverable than long-press-only. Taps on interactive content
      // (links, media players, reply quote, reaction chips) keep their own
      // behaviour, and a tap that just stopped a momentum fling is inert.
      if (
        !selectionMode &&
        !longPressFired.current &&
        !wasSwipe &&
        Date.now() - touchStartTime.current < 350 &&
        // A fling still in motion when the finger landed → this press is a
        // scroll-stop, not a tap. Snapshot taken at touchstart (see ref).
        touchStartTime.current - scrollTsAtTouchStart.current > 150
      ) {
        const target = e.target as HTMLElement;
        if (!target.closest(BUBBLE_INTERACTIVE_SELECTOR)) {
          // Suppress the browser's compatibility mouse events for this tap —
          // without this the synthesized `mousedown` (fired ~10-50ms later)
          // hits the document-level outside-close listener and dismisses the
          // menu the instant it opens. Also prevents the compat `click` from
          // triggering nested handlers.
          e.preventDefault();
          openMenuFromRect(e.currentTarget as HTMLElement);
        }
      }
    },
    [onReply, message, selectionMode, openMenuFromRect]
  );

  const metadata = useMemo(() => getMessageMetadata(message), [message]);
  const cleanText = stripMetaPrefix(message.text);
  const { preview: linkPreview, status: linkPreviewStatus } = useLinkPreview(cleanText || undefined);

  // Detect if message text is a direct image/GIF URL (e.g., from GIF picker)
  const imageUrlSrc = useMemo(() => {
    if (!cleanText) return null;
    const trimmed = cleanText.trim();
    if (/^https?:\/\/[^\s]+\.(gif|jpg|jpeg|png|webp)(\?[^\s]*)?$/i.test(trimmed)) return trimmed;
    return null;
  }, [cleanText]);

  const handleCopyText = useCallback(() => {
    if (cleanText) navigator.clipboard.writeText(cleanText).catch(() => {});
    closeCtxMenu();
  }, [cleanText, closeCtxMenu]);

  useEffect(() => {
    if (editing && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.setSelectionRange(editInputRef.current.value.length, editInputRef.current.value.length);
    }
  }, [editing]);

  const handleStartEdit = useCallback(() => {
    setEditText(cleanText);
    setEditing(true);
  }, [cleanText]);

  const handleCancelEdit = useCallback(() => {
    setEditing(false);
    setEditText(cleanText);
  }, [cleanText]);

  const handleSaveEdit = useCallback(async () => {
    const trimmed = editText.trim();
    if (!trimmed || trimmed === cleanText) {
      setEditing(false);
      return;
    }
    setEditSaving(true);
    try {
      const updated = await providers.MessagesProvider.editMessage({ messageId: message.id, text: trimmed });
      if (onMessageUpdated) onMessageUpdated(updated as any);
      setEditing(false);
    } catch (e) {
      console.error("Failed to edit message:", e);
    } finally {
      setEditSaving(false);
    }
  }, [editText, cleanText, message.id, onMessageUpdated]);

  const handleDelete = useCallback(async () => {
    if (!window.confirm(t("Delete this message?"))) return;
    try {
      await providers.MessagesProvider.delete({ id: message.id });
      setDeleted(true);
      if (onMessageDeleted) onMessageDeleted(message.id);
    } catch (e) {
      console.error("Failed to delete message:", e);
      // Surface the failure — silently swallowing it made "delete does
      // nothing" bugs undiagnosable for users.
      window.alert(t("Failed to delete the message. Please try again."));
    }
  }, [message.id, onMessageDeleted, t]);

  const handleEditKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSaveEdit();
      }
      if (e.key === "Escape") {
        handleCancelEdit();
      }
    },
    [handleSaveEdit, handleCancelEdit]
  );

  const handleToggleReaction = useCallback(
    async (emoji: string) => {
      const userId = authUser?.id || "";
      // Captured inside the updater so the rollback sees the CURRENT state,
      // not a stale closure ([authUser, message.id] deps).
      let previousReactions: any[] = [];
      // Optimistic local update
      setReactions((prev: any[]) => {
        previousReactions = prev;
        const existing = prev.find((r: any) => r.emoji === emoji);
        if (existing) {
          const hasUser = existing.userIds.includes(userId);
          if (hasUser) {
            const newUserIds = existing.userIds.filter((id: string) => id !== userId);
            if (newUserIds.length === 0) {
              return prev.filter((r: any) => r.emoji !== emoji);
            }
            return prev.map((r: any) => (r.emoji === emoji ? { ...r, count: r.count - 1, userIds: newUserIds } : r));
          } else {
            return prev.map((r: any) =>
              r.emoji === emoji ? { ...r, count: r.count + 1, userIds: [...r.userIds, userId] } : r
            );
          }
        } else {
          return [...prev, { emoji, count: 1, userIds: [userId] }];
        }
      });
      try {
        const updated = await providers.MessagesProvider.toggleReaction({ messageId: message.id, emoji });
        // Merge, don't replace — the server orders reactions differently,
        // and a wholesale replace made the chips visibly reorder a moment
        // after the click.
        setReactions((prev) => mergeReactionsKeepingOrder(prev, updated || []));
      } catch (e) {
        console.error("Failed to toggle reaction:", e);
        setReactions(previousReactions); // rollback on error
      }
    },
    [authUser, message.id]
  );

  const attachmentViews = useMemo(() => getAttachmentViews(message), [message]);
  // Voice / round-video players use the first (and only) attachment's URL.
  const firstFileUrl = attachmentViews[0]?.url;

  // Voice / round-video detection. The authoritative signal is
  // `metadata.voiceMessage` / `metadata.videoMessage` (set by MessageInput
  // when the user records). For forwards made BEFORE the metadata-preservation
  // fix (commit 94ccbc4) landed, the persisted __META__ has neither flag —
  // those messages can't be detected via metadata. Fall back to a filename +
  // MIME heuristic that matches the canonical names hardcoded in MessageInput
  // ("voice-message.webm" + audio/*  / "video-message.webm" + video/*). The
  // filename is not user-controllable through the UI (file picker uses the
  // uploaded file's real name, never these canonical ones), so the heuristic
  // is regression-safe for arbitrary user .webm uploads.
  const firstAttachment = attachmentViews[0];
  const isLikelyVoice =
    !!metadata?.voiceMessage ||
    (firstAttachment?.fileName === "voice-message.webm" && (firstAttachment?.mediaType || "").startsWith("audio/"));
  const isLikelyVideo =
    !!metadata?.videoMessage ||
    (firstAttachment?.fileName === "video-message.webm" && (firstAttachment?.mediaType || "").startsWith("video/"));

  // Tap toggles selection while in selection mode. We use onClick at the
  // wrapper level rather than per-element handlers because nested elements
  // (images, link previews, reply quotes) install their own listeners and
  // we want a uniform tap-anywhere-to-toggle behaviour. The transparent
  // overlay below absorbs the click so those nested handlers never fire.
  // NOTE: declared before any early return to keep hook order stable.
  const handleSelectionTap = useCallback(() => {
    if (selectionMode && onToggleSelection) onToggleSelection(message);
  }, [selectionMode, onToggleSelection, message]);

  if (deleted) return null;

  const cls = ["message-bubble"];
  if (isOwn) cls.push("__own");
  if (isStart) cls.push("__start");
  if (isEnd) cls.push("__end");
  if (selectionMode) cls.push("__selecting");
  if (selected) cls.push("__selected");
  // Round video notes render as a bare circle — no bubble chrome around it.
  if (isLikelyVideo) cls.push("__video-note");

  return (
    <div
      className={cls.join(" ")}
      // In selection mode the entire bubble row is tap-to-toggle. The
      // overlay below still exists to absorb clicks on nested interactive
      // elements (images, link previews, reply quotes); it has no handler
      // of its own — clicks bubble up to here, which avoids the
      // double-toggle that an overlay handler would cause.
      onClick={selectionMode ? handleSelectionTap : undefined}
      role={selectionMode ? "checkbox" : undefined}
      aria-checked={selectionMode ? !!selected : undefined}
      tabIndex={selectionMode ? 0 : undefined}
      onKeyDown={
        selectionMode
          ? (e) => {
              if (e.key === " " || e.key === "Enter") {
                e.preventDefault();
                handleSelectionTap();
              }
            }
          : undefined
      }
    >
      {selectionMode && (
        <div className={`message-bubble__selection-checkbox${selected ? " __on" : ""}`} aria-hidden>
          {selected && (
            <svg viewBox="0 0 24 24" width="14" height="14">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="currentColor" />
            </svg>
          )}
        </div>
      )}
      {!isOwn &&
        (showAvatar ? (
          <div className="message-bubble__avatar-col">
            <BaseAvatar user={message.senderUser} size={36} />
          </div>
        ) : (
          <div className="message-bubble__avatar-placeholder" />
        ))}
      {/* Swipe-to-reply indicator (outside wrapper so it doesn't move) */}
      {swipeOffset > 0 && (
        <div
          className={`message-bubble__swipe-reply-icon${swipeOffset >= swipeThreshold ? " __active" : ""}`}
          style={{ opacity: Math.min(swipeOffset / swipeThreshold, 1) }}
        >
          <svg viewBox="0 0 24 24" width="20" height="20">
            <path d="M10 9V5l-7 7 7 7v-4.1c5 0 8.5 1.6 11 5.1-1-5-4-10-11-11z" fill="currentColor" />
          </svg>
        </div>
      )}
      <div
        className="message-bubble__body-wrapper"
        onTouchStart={handleBubbleTouchStart}
        onTouchMove={handleBubbleTouchMove}
        onTouchEnd={handleBubbleTouchEnd}
        // Desktop: right-click opens the same action menu as mobile tap —
        // Telegram-style, replaces the old hover toolbar. The native context
        // menu is preserved where it matters: links, media (copy/save image),
        // form fields (paste in the edit textarea) and selected text (copy
        // selection).
        onContextMenu={(e) => {
          if (selectionMode) return;
          const target = e.target as HTMLElement;
          if (target.closest(BUBBLE_INTERACTIVE_SELECTOR)) return;
          if (window.getSelection()?.toString()) return;
          e.preventDefault();
          openMenuFromRect(e.currentTarget);
        }}
        style={swipeOffset > 0 ? { transform: `translateX(${swipeOffset}px)`, transition: "none" } : undefined}
      >
        {/* Selection-mode overlay: absorbs clicks on nested interactive
            elements (images, link previews, reply quotes) so they don't
            fire while multi-select is active. The click bubbles up to the
            root .message-bubble element, whose onClick handles the actual
            toggle — keeping a single source of truth for the tap action. */}
        {selectionMode && <div className="message-bubble__selection-overlay" aria-hidden />}
        {/* Context menu (long-press on mobile) — rendered via Portal to
            document.body so it escapes every ancestor stacking context
            (message list transforms, swipe-wrapper transforms, etc.) and
            always paints above every other message bubble, regardless of
            DOM order. */}
        {ctxAnchor &&
          !selectionMode &&
          createPortal(
            <div
              ref={ctxMenuRef}
              className="message-bubble__ctx-menu"
              // While we wait for useLayoutEffect to measure the real menu
              // height and compute `ctxMenu`, keep the node invisible at (0,0)
              // to avoid a one-frame flash at the wrong location.
              style={ctxMenu ? { top: ctxMenu.y, left: ctxMenu.x } : { top: 0, left: 0, visibility: "hidden" }}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
            >
              <div className="message-bubble__ctx-reactions">
                <ReactionPicker
                  onSelect={(emoji) => {
                    handleToggleReaction(emoji);
                    closeCtxMenu();
                  }}
                  onClose={() => {}}
                />
              </div>
              <div className="message-bubble__ctx-divider" />
              {onReply && (
                <button
                  type="button"
                  onClick={() => {
                    onReply(message);
                    closeCtxMenu();
                  }}
                >
                  <svg viewBox="0 0 24 24">
                    <path d="M10 9V5l-7 7 7 7v-4.1c5 0 8.5 1.6 11 5.1-1-5-4-10-11-11z" />
                  </svg>
                  {t("Reply")}
                </button>
              )}
              {onForward && (
                <button
                  type="button"
                  onClick={() => {
                    onForward(message);
                    closeCtxMenu();
                  }}
                >
                  <svg viewBox="0 0 24 24">
                    <path d="M14 9V5l7 7-7 7v-4.1c-5 0-8.5 1.6-11 5.1 1-5 4-10 11-11z" />
                  </svg>
                  {t("Forward")}
                </button>
              )}
              {onOpenThread && !metadata?.deletedForAll && (
                <button
                  type="button"
                  onClick={() => {
                    onOpenThread(message);
                    closeCtxMenu();
                  }}
                >
                  <svg viewBox="0 0 24 24">
                    <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z" />
                  </svg>
                  {t("Reply in thread")}
                </button>
              )}
              {cleanText && (
                <button type="button" onClick={handleCopyText}>
                  <svg viewBox="0 0 24 24">
                    <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
                  </svg>
                  {t("Copy")}
                </button>
              )}
              {onEnterSelection && (
                <button
                  type="button"
                  onClick={() => {
                    onEnterSelection(message);
                    closeCtxMenu();
                  }}
                >
                  <svg viewBox="0 0 24 24">
                    <path d="M19 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.11 0 2-.9 2-2V5c0-1.1-.89-2-2-2zm-9 14l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                  </svg>
                  {t("Select")}
                </button>
              )}
              {onPin && (
                <button
                  type="button"
                  onClick={() => {
                    onPin(message);
                    closeCtxMenu();
                  }}
                >
                  <svg viewBox="0 0 24 24">
                    <path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z" />
                  </svg>
                  {isPinned ? t("Unpin") : t("Pin")}
                </button>
              )}
              {isOwn && !editing && (
                <button
                  type="button"
                  onClick={() => {
                    handleStartEdit();
                    closeCtxMenu();
                  }}
                >
                  <svg viewBox="0 0 24 24">
                    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1.003 1.003 0 000-1.42l-2.34-2.34a1.003 1.003 0 00-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.82z" />
                  </svg>
                  {t("Edit")}
                </button>
              )}
              {canDelete && (
                <button
                  type="button"
                  className="__danger"
                  onClick={() => {
                    handleDelete();
                    closeCtxMenu();
                  }}
                >
                  <svg viewBox="0 0 24 24">
                    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                  </svg>
                  {t("Delete")}
                </button>
              )}
            </div>,
            document.body
          )}
        <div className="message-bubble__body" ref={rendered} id={`message-${message.id}`}>
          {/* Pinned indicator */}
          {isPinned && (
            <div className="message-bubble__pinned-badge">
              <svg viewBox="0 0 24 24" width="11" height="11">
                <path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z" fill="currentColor" />
              </svg>
              {t("Pinned")}
            </div>
          )}
          {/* Forwarded label */}
          {metadata?.forwardedFromName && (
            <div className="message-bubble__forwarded">
              <svg viewBox="0 0 24 24" width="12" height="12">
                <path d="M14 9V5l7 7-7 7v-4.1c-5 0-8.5 1.6-11 5.1 1-5 4-10 11-11z" fill="currentColor" />
              </svg>
              {t("Forwarded from {{name}}", { name: metadata.forwardedFromName })}
            </div>
          )}
          {showName && !isOwn && message.senderUser && (
            <div className="message-bubble__sender-name">{message.senderUser.screenName}</div>
          )}
          {/* Reply quote */}
          {metadata?.replyToMessageId && (
            <div
              className="message-bubble__reply-quote"
              onClick={() => {
                if (onQuoteClick) {
                  // Parent pages history + highlights via Virtuoso — works
                  // even when the quoted message isn't mounted (virtualized).
                  onQuoteClick(String(metadata.replyToMessageId));
                  return;
                }
                // Fallback (ThreadPanel etc.): best-effort DOM scroll.
                const el = document.querySelector(`[data-message-id="${metadata.replyToMessageId}"]`) as HTMLElement;
                if (el) {
                  el.scrollIntoView({ behavior: "smooth", block: "center" });
                  el.classList.add("message-bubble--highlight");
                  setTimeout(() => el.classList.remove("message-bubble--highlight"), 2000);
                }
              }}
            >
              <div className="message-bubble__reply-quote-accent" />
              <div className="message-bubble__reply-quote-body">
                <div className="message-bubble__reply-quote-name">{metadata.replyToSenderName || t("User")}</div>
                <div className="message-bubble__reply-quote-text">{metadata.replyToText || ""}</div>
              </div>
            </div>
          )}
          {/* Deleted placeholder */}
          {metadata?.deletedForAll ? (
            <div className="message-bubble__deleted">{t("This message was deleted")}</div>
          ) : editing ? (
            <div className="message-bubble__edit-form">
              <textarea
                ref={editInputRef}
                className="message-bubble__edit-input"
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                onKeyDown={handleEditKeyDown}
                disabled={editSaving}
              />
              <div className="message-bubble__edit-actions">
                <button
                  type="button"
                  className="message-bubble__edit-save"
                  onClick={handleSaveEdit}
                  disabled={editSaving}
                >
                  {editSaving ? "..." : t("Save")}
                </button>
                <button type="button" className="message-bubble__edit-cancel" onClick={handleCancelEdit}>
                  {t("Cancel")}
                </button>
              </div>
            </div>
          ) : imageUrlSrc ? (
            <>
              <img
                src={imageUrlSrc}
                alt=""
                className="message-bubble__inline-image"
                loading="lazy"
                // `width`/`height` hint the layout slot before the image
                // loads, so the bubble doesn't snap from 0 → ~300 px on
                // decode and yank the viewport. The matching min-height
                // reservation lives in `&__inline-image` SCSS.
                width={300}
                height={200}
                onClick={() => setLightboxOpen(true)}
              />
              {lightboxOpen && <ImageLightbox src={imageUrlSrc} alt="" onClose={() => setLightboxOpen(false)} />}
            </>
          ) : (
            cleanText && (
              <pre className="message-bubble__text">
                <MentionText text={cleanText} searchQuery={searchQuery} />
              </pre>
            )
          )}
          {/* Link preview — render an inline skeleton while the OG fetch is
              in flight so the bubble's height doesn't jump 200–260 px when
              the preview card lands (which would shift everything below
              under any mid-list reader, even with selective scroll-anchor). */}
          {linkPreviewStatus === "loading" && !editing && !metadata?.deletedForAll && (
            <div className="message-bubble__link-preview-skeleton" aria-hidden="true" />
          )}
          {linkPreview && !editing && !metadata?.deletedForAll && <LinkPreview preview={linkPreview} />}
          {/* Voice message player */}
          {isLikelyVoice && firstFileUrl && (
            <VoiceMessagePlayer
              src={firstFileUrl}
              duration={metadata?.voiceDuration}
              isOwn={isOwn}
              messageId={message.id}
              existingTranscription={metadata?.voiceTranscription}
              senderUserId={message.senderUser?.id}
            />
          )}
          {/* Round video message player */}
          {isLikelyVideo && firstFileUrl && (
            <VideoMessagePlayer src={firstFileUrl} duration={metadata?.videoDuration} />
          )}
          {/* Attachments — multi-file album grid + cards (skip for voice / round-video) */}
          {!isLikelyVoice && !isLikelyVideo && attachmentViews.length > 0 && (
            <MessageAttachments attachments={attachmentViews} />
          )}
          {reactions.length > 0 && (
            <ReactionBar reactions={reactions} currentUserId={authUser?.id || ""} onToggle={handleToggleReaction} />
          )}
          {threadReplyCount !== undefined && threadReplyCount > 0 && onOpenThread && (
            <div className="message-bubble__thread-indicator" onClick={() => onOpenThread(message)}>
              <svg viewBox="0 0 24 24" width="14" height="14">
                <path
                  d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"
                  fill="currentColor"
                />
              </svg>
              <span>
                {threadReplyCount} {threadReplyCount === 1 ? t("reply") : t("replies")}
              </span>
            </div>
          )}
          <span
            className={`message-bubble__meta${!isEnd ? " __hidden" : ""}${
              // Block content at the end of the bubble (reactions, attachment
              // grid, thread indicator) — meta gets its own row instead of
              // floating over that content.
              reactions.length > 0 ||
              (threadReplyCount !== undefined && threadReplyCount > 0 && !!onOpenThread) ||
              isLikelyVideo ||
              (!isLikelyVoice && attachmentViews.length > 0)
                ? " __below"
                : ""
            }`}
          >
            {(message as any).edited && (
              <span
                className="message-bubble__edited"
                onClick={() => setShowEditHistory(true)}
                style={{ cursor: "pointer" }}
              >
                {t("edited")}
              </span>
            )}
            <span className="message-bubble__time">{time}</span>
            {isOwn && (
              <span className="message-bubble__status">
                {(message as OptimisticMessage)._status === "sending" ? (
                  <span className="message-bubble__status-sending" title={t("Sending...")}>
                    <svg viewBox="0 0 16 16" width="14" height="14" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="8" cy="8" r="7" fill="none" stroke="#aab" strokeWidth="1.5" />
                      <line x1="8" y1="4" x2="8" y2="8.5" stroke="#aab" strokeWidth="1.5" strokeLinecap="round" />
                      <line x1="8" y1="8.5" x2="11" y2="8.5" stroke="#aab" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </span>
                ) : (message as OptimisticMessage)._status === "failed" ? (
                  <span className="message-bubble__status-failed" title={t("Failed to send")}>
                    <svg viewBox="0 0 16 16" width="14" height="14" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="8" cy="8" r="7" fill="none" stroke="#f44336" strokeWidth="1.5" />
                      <line x1="8" y1="4" x2="8" y2="9" stroke="#f44336" strokeWidth="1.5" strokeLinecap="round" />
                      <circle cx="8" cy="11.5" r="0.8" fill="#f44336" />
                    </svg>
                  </span>
                ) : opponentMinUnreadMessageId && Number(message.id) < Number(opponentMinUnreadMessageId) ? (
                  <CheckDouble read />
                ) : (
                  <CheckDouble />
                )}
              </span>
            )}
          </span>
        </div>
        {showEditHistory && (
          <EditHistoryModal edits={(message as any).editHistory || []} onClose={() => setShowEditHistory(false)} />
        )}
      </div>
    </div>
  );
}

function CheckDouble({ read = false }: { read?: boolean }) {
  return (
    <span className={`message-bubble__status-check${read ? " __read" : ""}`}>
      <svg viewBox="0 0 16 11" xmlns="http://www.w3.org/2000/svg">
        <path d="M11.071.653a.457.457 0 0 0-.304-.102.493.493 0 0 0-.381.178l-6.19 7.636-2.011-2.175a.463.463 0 0 0-.336-.153.457.457 0 0 0-.343.144.449.449 0 0 0-.107.373c.02.136.09.249.2.327l2.355 2.552a.464.464 0 0 0 .312.15h.036a.47.47 0 0 0 .34-.178l6.543-8.076a.45.45 0 0 0-.114-.676z" />
        <path d="M15.071.653a.457.457 0 0 0-.304-.102.493.493 0 0 0-.381.178l-6.19 7.636-1.2-1.298-.462.57 1.32 1.43a.464.464 0 0 0 .312.15h.036a.47.47 0 0 0 .34-.178l6.543-8.076a.45.45 0 0 0-.114-.676z" />
      </svg>
    </span>
  );
}

export default React.memo(MessageBubble);
